import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

const RED    = '#bf1e2e';
const NAVY   = '#014c8d';
const TEXT   = '#0d1b2a';
const DIM    = 'rgba(13,27,42,0.55)';
const DIM2   = 'rgba(13,27,42,0.35)';
const SURFACE = '#f4f6fa';
const BORDER  = 'rgba(0,0,0,0.09)';
const DESIGN_W = 960;
const DESIGN_H = Math.round(DESIGN_W * 9 / 16);
const PASSWORD = 'institutions2026';

type SlideId = 'cover' | 'problem' | 'os' | 'product' | 'programs' | 'licensing' | 'earlypathways' | 'onboarding' | 'team' | 'close';
interface Slide { id: SlideId; label: string; }

const SLIDES: Slide[] = [
  { id: 'cover',       label: '01  Cover' },
  { id: 'problem',     label: '02  The Reality' },
  { id: 'os',          label: '03  The Platform' },
  { id: 'product',     label: '04  In Action' },
  { id: 'programs',    label: '05  Four Programs' },
  { id: 'licensing',      label: '06  Licensing' },
  { id: 'earlypathways',  label: '07  Early Pathways' },
  { id: 'onboarding',     label: '08  Go Live' },
  { id: 'team',           label: '09  Team' },
  { id: 'close',          label: '10  Close' },
];

function ScaledSlide({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / DESIGN_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: '100%', height: DESIGN_H * scale, position: 'relative' }}>
      <div style={{ width: DESIGN_W, height: DESIGN_H, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'absolute', top: 0, left: 0 }}>
        {children}
      </div>
    </div>
  );
}

function BthdMark({ markHeight, filter }: { markHeight: number; filter?: string }) {
  const canvasH  = markHeight / 0.32;
  const markW    = canvasH * 0.82;
  const clipTop  = canvasH * 0.34;
  const clipLeft = canvasH * 0.09;
  return (
    <div style={{ height: markHeight, width: markW, overflow: 'hidden', flexShrink: 0, position: 'relative' }}>
      <img src="/bthd-icon.png" alt="BTHD" style={{ height: canvasH, width: 'auto', position: 'absolute', top: -clipTop, left: -clipLeft, display: 'block', filter: filter ?? 'brightness(0) opacity(0.5)' }} />
    </div>
  );
}

function SlideShell({ tag, children, center = false }: { tag?: string; children: React.ReactNode; center?: boolean }) {
  return (
    <div style={{
      width: '100%', height: '100%', background: '#fff',
      display: 'flex', flexDirection: 'column',
      justifyContent: center ? 'center' : 'flex-start',
      alignItems: center ? 'center' : 'stretch',
      padding: '44px 60px', position: 'relative',
      overflow: 'hidden', boxSizing: 'border-box',
    }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'radial-gradient(rgba(0,0,0,0.032) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: NAVY }} />
      <div style={{ position: 'absolute', top: 0, left: 0, width: 80, height: 4, background: RED }} />
      {tag && (
        <div style={{ position: 'absolute', top: 14, left: 60, fontSize: 9, fontWeight: 800, letterSpacing: 3.5, color: NAVY, textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif" }}>{tag}</div>
      )}
      <div style={{ position: 'absolute', bottom: 10, left: 60, right: 48, display: 'flex', alignItems: 'center' }}>
        <BthdMark markHeight={13} />
        <div style={{ width: 1, height: 12, background: 'rgba(0,0,0,0.14)', margin: '0 8px' }} />
        <span style={{ fontSize: 9, fontWeight: 600, color: DIM2, letterSpacing: 0.4 }}>Bigger Than Hoop Dreams Inc.</span>
      </div>
      {children}
    </div>
  );
}

function Heading({ children, size = 44 }: { children: React.ReactNode; size?: number }) {
  return (
    <h2 style={{ margin: '0 0 8px', fontSize: size, fontWeight: 900, lineHeight: 1.0, color: TEXT, textTransform: 'uppercase', letterSpacing: -0.5, fontFamily: "'Barlow Condensed', sans-serif" }}>
      {children}
    </h2>
  );
}

function Eyebrow({ children, navy = false }: { children: React.ReactNode; navy?: boolean }) {
  const c = navy ? NAVY : RED;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', background: `${c}14`, border: `1px solid ${c}40`, borderRadius: 4, padding: '3px 10px', marginBottom: 12, fontSize: 9, fontWeight: 800, letterSpacing: 2, color: c, textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif" }}>
      {children}
    </div>
  );
}

function PhoneMockup({ screenshot, alt }: { screenshot: string; alt: string }) {
  return (
    <div style={{
      width: 130, height: 278,
      background: '#080808',
      borderRadius: 28,
      border: '1.5px solid rgba(255,255,255,0.14)',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.55), 0 16px 40px rgba(0,0,0,0.25)',
      padding: '7px 4px 6px',
      position: 'relative', boxSizing: 'border-box', flexShrink: 0,
    }}>
      <div style={{ position: 'absolute', top: 9, left: '50%', transform: 'translateX(-50%)', width: 28, height: 7, background: '#000', borderRadius: 4, zIndex: 3 }} />
      <div style={{ width: '100%', height: '100%', borderRadius: 22, overflow: 'hidden', background: '#000' }}>
        <img src={screenshot} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
      </div>
    </div>
  );
}

// ── 01 COVER ──────────────────────────────────────────────────────────────────
function CoverSlide() {
  return (
    <div style={{ width: '100%', height: '100%', background: '#fff', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', boxSizing: 'border-box' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: NAVY }} />
      <div style={{ position: 'absolute', top: 0, left: 0, width: 80, height: 4, background: RED }} />
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, background: NAVY }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(0,0,0,0.03) 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%,-50%)', width: 560, height: 320, background: `radial-gradient(ellipse, ${NAVY}0d 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '52px 80px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <img src="/bthd-email-sig-300x80-transparent.png" alt="BTHD" style={{ height: 42, width: 'auto', marginBottom: 28, filter: 'brightness(0) opacity(0.88)' }} />
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 4, color: RED, textTransform: 'uppercase', marginBottom: 18, fontFamily: "'Barlow Condensed', sans-serif" }}>Institutional Partnership Overview</div>
        <h1 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 62, fontWeight: 900, lineHeight: 0.96, color: TEXT, textTransform: 'uppercase', letterSpacing: -1.5, margin: '0 0 20px' }}>
          Athlete Development<br /><span style={{ color: NAVY }}>and Transition Platform</span>
        </h1>
        <div style={{ width: 56, height: 3, background: RED, margin: '0 auto 20px' }} />
        <p style={{ fontSize: 15, color: TEXT, fontStyle: 'italic', letterSpacing: 0.3, margin: '0 0 28px' }}>"A future where every athlete has a plan, a pathway, and a purpose."</p>
        <div style={{ fontSize: 11, color: NAVY, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>bthd.app</div>
      </div>
      <div style={{ height: 30, background: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
        <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.65)', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600 }}>Bigger Than Hoop Dreams Inc. · Kitchener, Ontario, Canada</span>
      </div>
    </div>
  );
}

// ── 02 PROBLEM ────────────────────────────────────────────────────────────────
function ProblemSlide() {
  const stats = [
    { num: '39%', label: 'experience identity loss or psychological distress post career', src: 'British Journal of Sports Medicine, 2019' },
    { num: '16%', label: 'face financial distress within 5 years of retirement', src: 'NBER, 2015' },
    { num: '57.3%', label: 'of student-athletes said their transition from sport was a negative experience', src: 'Stokowski et al., 2019, Journal of Issues in Intercollegiate Athletics' },
  ];
  const impacts = [
    { label: 'Recruitment', copy: 'Families increasingly choose programs based on what happens after sport, not just what happens on the field.' },
    { label: 'Duty of Care', copy: 'Regulators, governing bodies, and athletes themselves are asking what institutions did for their athletes after the final game.' },
    { label: 'Reputational Risk', copy: 'When a former athlete\'s story goes wrong, the institution that developed them is part of that story.' },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 20 }}>
        <Heading size={40}>Athletes Are Falling Off A Cliff.<br /><span style={{ color: RED }}>With No Net.</span></Heading>
        <div style={{ display: 'flex', gap: 13, margin: '14px 0 13px' }}>
          {stats.map(({ num, label, src }) => (
            <div key={num} style={{ flex: 1, background: SURFACE, border: `1px solid ${BORDER}`, borderTop: `3px solid ${RED}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 46, fontWeight: 900, color: RED, lineHeight: 1, fontFamily: "'Barlow Condensed', sans-serif" }}>{num}</div>
              <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.5, margin: '6px 0 5px' }}>{label}</div>
              <div style={{ fontSize: 10.5, color: DIM, fontWeight: 700, letterSpacing: 0.3 }}>{src}</div>
            </div>
          ))}
        </div>
        <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
          <div style={{ fontSize: 17, fontWeight: 900, color: TEXT, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 13, fontFamily: "'Barlow Condensed', sans-serif" }}>
            When athletes fall, your program feels it.
          </div>
          <div style={{ display: 'flex', gap: 13 }}>
            {impacts.map(({ label, copy }) => (
              <div key={label} style={{ flex: 1, background: SURFACE, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${NAVY}`, borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: NAVY, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6, fontFamily: "'Barlow Condensed', sans-serif" }}>{label}</div>
                <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.55 }}>{copy}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideShell>
  );
}

// ── 03 PLATFORM / OS ─────────────────────────────────────────────────────────
function OsSlide() {
  const stages = [
    { num: '01', title: 'Athlete Assessment', body: 'Identity, strengths, and athletic purpose.', color: RED },
    { num: '02', title: 'Skills and Pathways', body: 'Transferable skills mapping and career pathway awareness.', color: NAVY },
    { num: '03', title: 'Leadership Development', body: 'Communication, influence, team dynamics, and personal brand.', color: '#1a6b3c' },
    { num: '04', title: 'Lasting Impact', body: 'Legacy planning and life after sport.', color: '#6b3c8a' },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 20, display: 'flex', gap: 40, height: 'calc(100% - 52px)' }}>
        <div style={{ flex: '0 0 290px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Heading size={38}>A Structured Operating System Preparing Athletes for Life After Sport.</Heading>
          <p style={{ fontSize: 13, color: TEXT, lineHeight: 1.7, marginTop: 10 }}>
            BTHD gives your program a structured, measurable journey athletes can start from day one, built and maintained by BTHD, with no curriculum to build yourself.
          </p>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10 }}>
          {stages.map(({ num, title, body, color }) => (
            <div key={num} style={{ display: 'flex', alignItems: 'center', gap: 16, background: SURFACE, border: `1px solid ${BORDER}`, borderLeft: `4px solid ${color}`, borderRadius: 8, padding: '13px 16px' }}>
              <div style={{ fontSize: 26, fontWeight: 900, color, lineHeight: 1, fontFamily: "'Barlow Condensed', sans-serif", flexShrink: 0, width: 28 }}>{num}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 900, color: TEXT, fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.15 }}>{title}</div>
                <div style={{ fontSize: 11.5, color: TEXT, lineHeight: 1.5, marginTop: 3 }}>{body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

// ── 04 PRODUCT / IN ACTION ────────────────────────────────────────────────────
function ProductSlide() {
  const stages = [
    { num: '01', label: 'Athlete Assessment',     color: RED,       screenshot: '/screenshots/07-identity-profile.jpg' },
    { num: '02', label: 'Skills and Pathways',    color: NAVY,      screenshot: '/screenshots/08-transition-path.jpg'  },
    { num: '03', label: 'Leadership Development', color: '#1a6b3c', screenshot: '/screenshots/03-leadership.jpg'       },
    { num: '04', label: 'Lasting Impact',         color: '#6b3c8a', screenshot: '/screenshots/05-legacy.jpg'           },
  ];
  return (
    <SlideShell tag="Product">
      <div style={{ marginTop: 16 }}>
        <Heading size={38}>Four Stages.<br /><span style={{ color: NAVY }}>One Athlete Journey.</span></Heading>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 16 }}>
          {stages.map(({ num, label, color, screenshot }) => (
            <div key={num} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <PhoneMockup screenshot={screenshot} alt={label} />
              <div style={{ marginTop: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 800, color, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2, fontFamily: "'Barlow Condensed', sans-serif" }}>{num}</div>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: TEXT, letterSpacing: 0.8, textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif" }}>{label}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 8.5, color: DIM, letterSpacing: 0.5 }}>
          5-Dimension Behavioral Assessment · Transferable Skills · AI-Powered Athlete Development · Sport to Career Mapping and Transition Platform
        </div>
      </div>
    </SlideShell>
  );
}

// ── 05 PROGRAMS ───────────────────────────────────────────────────────────────
function ProgramsSlide() {
  const programs = [
    {
      code: 'P01', name: 'Behavioural Program', tier: 'Starter and above', weeks: 15,
      phases: ['Athlete Assessment', 'Skills and Pathways', 'Leadership Development', 'Lasting Impact'],
      color: RED,
    },
    {
      code: 'P02', name: 'Post Sport Development', tier: 'Pro and above', weeks: 15,
      phases: ['Redefining Who You Are', 'Discovering Life Without Sport', 'Rebuilding Purpose and Structure', 'Relationships and Belonging', 'Defining Legacy and Next Chapter'],
      color: NAVY,
    },
    {
      code: 'P03', name: 'Career Development', tier: 'Elite', weeks: 15,
      phases: ['Transferable Skills Audit', 'Industry Mapping and Career Selection', 'Resume, LinkedIn, and Positioning', 'Networking, Outreach, and Interviews', 'Offer Evaluation and First 90 Days'],
      color: '#1a3a6b',
    },
    {
      code: 'P04', name: 'Financial Literacy', tier: 'Elite', weeks: 15,
      phases: ['Your Relationship With Money', 'Understanding What You Have', 'Building Financial Structure', "Protecting What You've Built", 'Wealth Beyond the Game'],
      color: '#c8962a',
    },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 20 }}>
        <Heading size={40}>One Platform.<br /><span style={{ color: RED }}>Four Programs.</span></Heading>
        <p style={{ fontSize: 13, color: TEXT, lineHeight: 1.65, maxWidth: 620, margin: '8px 0 14px' }}>
          Each program builds on the last. Institutions unlock one, two, or all four programs based on their license tier.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 13 }}>
          {programs.map(({ code, name, tier, weeks, phases, color }) => (
            <div key={code} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderTop: `4px solid ${color}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '13px 15px 10px', borderBottom: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 28, fontWeight: 900, color, lineHeight: 1, fontFamily: "'Barlow Condensed', sans-serif" }}>{code}</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: TEXT, textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif", lineHeight: 1.1, marginTop: 4 }}>{name}</div>
                <div style={{ fontSize: 10.5, color: DIM, marginTop: 4 }}>{weeks} weeks · {tier}</div>
              </div>
              <div style={{ padding: '10px 15px 12px' }}>
                {phases.map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '3px 0' }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 5 }} />
                    <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.45 }}>{p}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

// ── 09 LICENSING ──────────────────────────────────────────────────────────────
function LicensingSlide() {
  const tiers = [
    {
      name: 'Starter', price: '$10,000', programs: 'P01 Behavioural Program', color: RED, featured: false,
      features: ['Up to 15 athletes', '$50 per additional athlete', 'Institution admin dashboard', 'Assessment tracking', 'Assessment reporting', 'Quarterly cohort reports'],
    },
    {
      name: 'Pro', price: '$15,000', programs: 'P01 + P02 Programs', color: NAVY, featured: true,
      features: ['Everything in Starter', 'Post Sport Development program', 'Progress tracking across programs', 'Monthly cohort reports', 'Priority support'],
    },
    {
      name: 'Elite', price: '$25,000', programs: 'All Four Programs', color: TEXT, featured: false,
      features: ['Everything in Pro', 'Career Development program', 'Financial Literacy program', 'Full platform access', 'Custom onboarding support', 'Dedicated success contact'],
    },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 20 }}>
        <Heading size={42}>Institution Licensing.<br /><span style={{ color: NAVY }}>Investment Tiers.</span></Heading>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 13, marginTop: 16 }}>
          {tiers.map(({ name, price, programs, color, featured, features }) => (
            <div key={name} style={{ background: featured ? `${NAVY}08` : SURFACE, border: `1px solid ${featured ? NAVY + '44' : BORDER}`, borderTop: `4px solid ${color}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '13px 17px 10px', borderBottom: `1px solid ${featured ? NAVY + '22' : BORDER}` }}>
                {featured && <div style={{ fontSize: 9, fontWeight: 800, color: NAVY, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 2, fontFamily: "'Barlow Condensed', sans-serif" }}>Most Popular</div>}
                <div style={{ fontSize: 17, fontWeight: 900, color: TEXT, textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif" }}>{name}</div>
                <div style={{ fontSize: 32, fontWeight: 900, color, fontFamily: "'Barlow Condensed', sans-serif", lineHeight: 1.1, marginTop: 6 }}>{price}</div>
                <div style={{ fontSize: 9, color: DIM2, letterSpacing: 1, marginTop: 2 }}>CAD / YEAR</div>
                <div style={{ fontSize: 10, color: TEXT, marginTop: 6, fontWeight: 600 }}>{programs}</div>
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: '11px 17px 13px' }}>
                {features.map(f => (
                  <li key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '3px 0' }}>
                    <span style={{ color: featured ? NAVY : RED, fontWeight: 700, fontSize: 10, marginTop: 1 }}>✓</span>
                    <span style={{ fontSize: 11.5, color: TEXT, lineHeight: 1.45 }}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 10, color: DIM2, fontStyle: 'italic', marginTop: 10 }}>All pricing in CAD. Annual license. Multi-year commitments available at preferred rates.</div>
      </div>
    </SlideShell>
  );
}

// ── 08 EARLY PATHWAYS ─────────────────────────────────────────────────────────
function EarlyPathwaysSlide() {
  const tiers = [
    {
      name: 'Tier 1', price: '$10,000', featured: false, color: RED, desc: 'Foundation',
      features: ['Up to 15 athletes', '$50 per additional athlete', 'All four Early Pathways stages', 'Parent and athlete co-entry at early ages', 'Annual platform license'],
    },
    {
      name: 'Tier 2', price: '$15,000', featured: true, color: NAVY, desc: 'Organization Standard',
      features: ['Everything in Tier 1', 'Organization admin dashboard', 'Athlete roster and progress tracking', 'Cohort completion reporting'],
    },
    {
      name: 'Tier 3', price: '$22,500', featured: false, color: TEXT, desc: 'Full Engagement',
      features: ['Everything in Tier 2', 'In-person engagement sessions', 'Camps, events, and panels', 'Two annual touchpoints included'],
    },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 20, display: 'flex', gap: 36, height: 'calc(100% - 52px)', alignItems: 'center' }}>

        {/* Left */}
        <div style={{ flex: '0 0 210px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Eyebrow>Youth Sport Organizations</Eyebrow>
          <Heading size={30}>A Structured 4-Stage Sport Development Program for Youth Sport Organizations.</Heading>
          <p style={{ fontSize: 12, color: TEXT, lineHeight: 1.7, marginTop: 10 }}>
            Built to give young athletes more than game reps; identity, character, and NIL readiness.
          </p>
          <a href="https://bthd.app/early-pathways-program" target="_blank" rel="noopener noreferrer" style={{ marginTop: 16, fontSize: 11, fontWeight: 700, color: NAVY, textDecoration: 'none', letterSpacing: 0.5 }}>
            bthd.app/early-pathways-program →
          </a>
        </div>

        {/* Right — pricing tiers styled like Licensing slide */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tiers.map(({ name, price, featured, color, desc, features }) => (
            <div key={name} style={{ background: featured ? `${NAVY}08` : SURFACE, border: `1px solid ${featured ? NAVY + '44' : BORDER}`, borderTop: `4px solid ${color}`, borderRadius: 10, overflow: 'hidden', display: 'flex' }}>
              {/* Price block */}
              <div style={{ padding: '12px 16px', borderRight: `1px solid ${featured ? NAVY + '22' : BORDER}`, flexShrink: 0, width: 130, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {featured && <div style={{ fontSize: 8, fontWeight: 800, color: NAVY, letterSpacing: 1.5, textTransform: 'uppercase' as const, marginBottom: 3, fontFamily: "'Barlow Condensed', sans-serif" }}>Most Popular</div>}
                <div style={{ fontSize: 14, fontWeight: 900, color: TEXT, textTransform: 'uppercase' as const, fontFamily: "'Barlow Condensed', sans-serif" }}>{name}</div>
                <div style={{ fontSize: 26, fontWeight: 900, color, fontFamily: "'Barlow Condensed', sans-serif", lineHeight: 1.1, marginTop: 3 }}>{price}</div>
                <div style={{ fontSize: 8.5, color: DIM2, letterSpacing: 0.8, marginTop: 1 }}>CAD / YEAR</div>
                <div style={{ fontSize: 9.5, color: TEXT, marginTop: 5, fontWeight: 600 }}>{desc}</div>
              </div>
              {/* Features — 2-column grid */}
              <div style={{ flex: 1, padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px', alignContent: 'center' }}>
                {features.map(f => (
                  <div key={f} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <span style={{ color: featured ? NAVY : color, fontWeight: 700, fontSize: 10, marginTop: 2, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 11, color: TEXT, lineHeight: 1.4 }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ fontSize: 10, color: DIM2, fontStyle: 'italic', marginTop: 2 }}>All pricing in CAD. Annual license.</div>
        </div>

      </div>
    </SlideShell>
  );
}

// ── 09 ONBOARDING ─────────────────────────────────────────────────────────────
function OnboardingSlide() {
  const steps = [
    { num: '01', title: 'License and Setup', copy: 'Agreement signed, institution profile configured, admin accounts provisioned.' },
    { num: '02', title: 'Orientation Call', copy: 'BTHD walks your team through the platform and program structure.' },
    { num: '03', title: 'Athlete Portal Access', copy: 'Athlete credentials validated for licensed institution access.' },
    { num: '04', title: 'First Cohort Launches', copy: 'Athletes are active. Progress visible from day one.' },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 20, display: 'flex', gap: 44, height: 'calc(100% - 52px)', alignItems: 'center' }}>
        <div style={{ flex: '0 0 270px' }}>
          <Heading size={42}>You Could Be<br /><span style={{ color: RED }}>Live in Weeks.</span></Heading>
          <p style={{ fontSize: 13, color: TEXT, lineHeight: 1.7, marginTop: 10 }}>
            Most institutions are fully operational within 30 days of signing. BTHD handles the technical setup so your team can focus on athletes.
          </p>
          <div style={{ marginTop: 16, padding: '12px 15px', background: `${NAVY}0c`, border: `1px solid ${NAVY}2a`, borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: NAVY, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, fontFamily: "'Barlow Condensed', sans-serif" }}>What We Handle</div>
            <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.55 }}>Technical setup, portal configuration, onboarding support, and ongoing success check-ins.</div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 11 }}>
          {steps.map(({ num, title, copy }, i) => (
            <div key={num} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: i === 0 ? RED : '#fff', border: `2px solid ${i === 0 ? RED : BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 900, color: i === 0 ? '#fff' : DIM, fontFamily: "'Barlow Condensed', sans-serif" }}>{num}</span>
              </div>
              <div style={{ flex: 1, background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 15px' }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: TEXT, textTransform: 'uppercase', letterSpacing: 0.3, fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.5 }}>{copy}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

// ── 11 TEAM ───────────────────────────────────────────────────────────────────
function PersonCard({ photo, initials, accentColor, name, role, bio, linkedin }: {
  photo: string; initials: string; accentColor: string; name: string; role: string; bio: string; linkedin: string;
}) {
  const [imgError, setImgError] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', background: SURFACE, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${accentColor}`, borderRadius: 10, padding: '16px 18px' }}>
      {!imgError ? (
        <img src={photo} alt={name} onError={() => setImgError(true)} style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${accentColor}` }} />
      ) : (
        <div style={{ width: 56, height: 56, borderRadius: '50%', flexShrink: 0, background: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 18, color: '#fff' }}>{initials}</div>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 17, color: TEXT, marginBottom: 2 }}>{name}</div>
        <div style={{ fontSize: 9, fontWeight: 800, color: accentColor, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 7, fontFamily: "'Barlow Condensed', sans-serif" }}>{role}</div>
        <div style={{ fontSize: 11.5, color: TEXT, lineHeight: 1.6, marginBottom: 7 }}>{bio}</div>
        <a href={linkedin} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, fontWeight: 700, color: NAVY, textDecoration: 'none', letterSpacing: 0.5 }}>LinkedIn →</a>
      </div>
    </div>
  );
}

function TeamSlide() {
  return (
    <SlideShell>
      <div style={{ marginTop: 24, display: 'flex', gap: 44, alignItems: 'flex-start', height: 'calc(100% - 56px)' }}>
        <div style={{ flex: '0 0 240px', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', paddingBottom: 28 }}>
          <Eyebrow>Who We Are</Eyebrow>
          <Heading size={38}>Built By People<br /><span style={{ color: RED }}>Who Lived This.</span></Heading>
          <p style={{ fontSize: 12, color: TEXT, lineHeight: 1.65, marginTop: 10 }}>
            BTHD was founded by athletes, for athletes. The team brings together lived experience in sport with expertise in technology, sport business, corporate finance and community building.
          </p>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 13, justifyContent: 'center', height: '100%', paddingBottom: 28 }}>
          <PersonCard
            photo="/harry.jpg" initials="HE" accentColor={RED}
            name="Harry Ezenibe" role="Founder"
            bio="Former athlete and corporate and tech leader. U SPORTS (Saint Mary's University), NBA Basketball Without Borders, Toronto Raptors Front Office Player Development. Harry founded BTHD to create the platform he wished existed."
            linkedin="https://www.linkedin.com/in/harry-ezenibe"
          />
          <PersonCard
            photo="/osa.jpg" initials="OW" accentColor={NAVY}
            name="Osa Wilson" role="Athlete Legacy Ambassador"
            bio="NCAA and CEBL athlete turned financial industry professional. Living proof that the BTHD framework works, directly mentored by Harry through the BTHD methodology from athlete to thriving professional."
            linkedin="https://www.linkedin.com/in/osa-wilson-a9308121b"
          />
        </div>
      </div>
    </SlideShell>
  );
}

// ── 12 CLOSE ──────────────────────────────────────────────────────────────────
function CloseSlide() {
  return (
    <SlideShell center>
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1, maxWidth: 680, margin: '0 auto' }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 4, color: RED, textTransform: 'uppercase', marginBottom: 20, fontFamily: "'Barlow Condensed', sans-serif" }}>Let's Get to Work</div>
        <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 42, fontWeight: 900, lineHeight: 1.08, color: TEXT, textTransform: 'uppercase', letterSpacing: -0.5, margin: '0 0 24px' }}>
          Let's build a plan, a pathway, and a lasting purpose for the athletes in your program, and beyond their playing career.
        </h2>
        <div style={{ width: 56, height: 3, background: RED, margin: '0 auto 28px' }} />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
          <a href="mailto:harry@bthd.app" style={{ display: 'inline-block', padding: '10px 20px', background: 'transparent', border: `1.5px solid ${NAVY}`, color: NAVY, fontSize: 13, fontWeight: 700, textDecoration: 'none', borderRadius: 6, letterSpacing: 0.5, fontFamily: "'Barlow Condensed', sans-serif" }}>harry@bthd.app</a>
          <a href="https://cal.com/bigger-than-hoop-dreams" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', padding: '10px 22px', background: RED, color: '#fff', fontSize: 13, fontWeight: 800, textDecoration: 'none', borderRadius: 6, letterSpacing: 0.8, fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' as const }}>Book a Call with BTHD</a>
          <a href="https://bthd.app" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', padding: '10px 20px', background: 'transparent', border: `1.5px solid ${NAVY}`, color: NAVY, fontSize: 13, fontWeight: 700, textDecoration: 'none', borderRadius: 6, letterSpacing: 0.5, fontFamily: "'Barlow Condensed', sans-serif" }}>bthd.app</a>
        </div>
      </div>
    </SlideShell>
  );
}

// ── Component map (for PDF export) ────────────────────────────────────────────
const SLIDE_COMPONENTS: Record<SlideId, React.FC> = {
  cover:       CoverSlide,
  problem:     ProblemSlide,
  os:          OsSlide,
  product:     ProductSlide,
  programs:    ProgramsSlide,
  licensing:      LicensingSlide,
  earlypathways:  EarlyPathwaysSlide,
  onboarding:     OnboardingSlide,
  team:        TeamSlide,
  close:       CloseSlide,
};

function renderSlide(id: SlideId) {
  const Comp = SLIDE_COMPONENTS[id];
  return <Comp />;
}

// ── Password gate ─────────────────────────────────────────────────────────────
function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const attempt = () => {
    if (value.trim() === PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setValue('');
      setTimeout(() => setError(false), 1400);
    }
  };
  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow', sans-serif" }}>
      <div style={{ width: 380, textAlign: 'center', background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderTop: `4px solid ${NAVY}`, borderRadius: 12, padding: '44px 40px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
        <img src="/bthd-email-sig-300x80-transparent.png" alt="BTHD" style={{ height: 34, marginBottom: 24, filter: 'brightness(0) opacity(0.85)' }} />
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 3, color: RED, textTransform: 'uppercase', marginBottom: 6 }}>Confidential</div>
        <div style={{ fontSize: 19, fontWeight: 900, color: TEXT, marginBottom: 28, fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>Institution Partnership Deck</div>
        <input
          type="password" value={value} placeholder="Enter access code"
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && attempt()}
          style={{ width: '100%', padding: '12px 14px', background: '#f4f6fa', border: `1px solid ${error ? RED : 'rgba(0,0,0,0.12)'}`, borderRadius: 8, color: TEXT, fontSize: 14, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s', fontFamily: 'inherit' }}
        />
        {error && <div style={{ fontSize: 12, color: RED, marginTop: 8 }}>Incorrect access code.</div>}
        <button onClick={attempt} style={{ marginTop: 14, width: '100%', padding: '12px', background: NAVY, border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5, fontFamily: 'inherit' }}>
          Access Deck
        </button>
        <div style={{ fontSize: 11, color: TEXT, marginTop: 20, lineHeight: 1.7 }}>For authorized recipients only.</div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function InstitutionsDeckPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [current, setCurrent] = useState<SlideId>('cover');
  const [exporting, setExporting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const idx  = SLIDES.findIndex(s => s.id === current);
  const prev = idx > 0 ? SLIDES[idx - 1].id : null;
  const next = idx < SLIDES.length - 1 ? SLIDES[idx + 1].id : null;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!unlocked) return;
      if (e.key === 'Escape') { setFullscreen(false); return; }
      if (e.key === 'ArrowRight') setCurrent(s => { const i = SLIDES.findIndex(sl => sl.id === s); return i < SLIDES.length - 1 ? SLIDES[i + 1].id : s; });
      if (e.key === 'ArrowLeft')  setCurrent(s => { const i = SLIDES.findIndex(sl => sl.id === s); return i > 0 ? SLIDES[i - 1].id : s; });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [unlocked]);

  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
      const wrapper = document.createElement('div');
      wrapper.style.cssText = `position:fixed;top:0;left:100vw;width:${DESIGN_W}px;height:${DESIGN_H}px;overflow:hidden;z-index:-1;font-family:'Barlow','Barlow Condensed',sans-serif;`;
      document.body.appendChild(wrapper);
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [DESIGN_W, DESIGN_H], compress: true });
      for (let i = 0; i < SLIDES.length; i++) {
        const SlideComp = SLIDE_COMPONENTS[SLIDES[i].id];
        wrapper.innerHTML = '';
        const el = document.createElement('div');
        el.style.cssText = `width:${DESIGN_W}px;height:${DESIGN_H}px;`;
        wrapper.appendChild(el);
        const root = createRoot(el);
        root.render(<SlideComp />);
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        await new Promise(r => setTimeout(r, 200));
        const canvas = await html2canvas(el, { width: DESIGN_W, height: DESIGN_H, backgroundColor: '#ffffff', scale: 2, useCORS: true, logging: false });
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.93), 'JPEG', 0, 0, DESIGN_W, DESIGN_H);
        root.unmount();
      }
      document.body.removeChild(wrapper);
      pdf.save('bthd-institution-deck.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  const ActiveComponent = SLIDE_COMPONENTS[current];

  return (
    <div style={{ minHeight: '100vh', background: '#e8eaf0', fontFamily: "'Barlow Condensed', 'Barlow', sans-serif", display: 'flex', flexDirection: 'column' }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', background: '#0a101e', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 24px', height: 48, flexShrink: 0, overflowX: 'auto' }}>
        <div style={{ marginRight: 14 }}>
          <BthdMark markHeight={18} filter="brightness(0) invert(1) opacity(0.75)" />
        </div>
        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', marginRight: 4, flexShrink: 0 }} />
        {SLIDES.map(s => (
          <button key={s.id} onClick={() => setCurrent(s.id)} style={{ padding: '0 13px', height: '100%', background: 'none', border: 'none', borderBottom: `2px solid ${s.id === current ? '#4a9eff' : 'transparent'}`, color: s.id === current ? '#fff' : 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: s.id === current ? 800 : 600, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: 0.5, fontFamily: "'Barlow Condensed', sans-serif", transition: 'color 0.15s' }}>
            {s.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0, paddingLeft: 16 }}>
          <button onClick={() => prev && setCurrent(prev)} disabled={!prev} style={{ padding: '4px 12px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: prev ? '#fff' : 'rgba(255,255,255,0.2)', fontSize: 12, cursor: prev ? 'pointer' : 'default', fontFamily: 'inherit' }}>←</button>
          <button onClick={() => next && setCurrent(next)} disabled={!next} style={{ padding: '4px 12px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: next ? '#fff' : 'rgba(255,255,255,0.2)', fontSize: 12, cursor: next ? 'pointer' : 'default', fontFamily: 'inherit' }}>→</button>
          <button onClick={() => setFullscreen(true)} style={{ padding: '4px 12px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>⛶</button>
          <button onClick={handleExportPDF} disabled={exporting} style={{ padding: '4px 12px', background: exporting ? 'rgba(255,255,255,0.04)' : 'rgba(191,30,46,0.8)', border: '1px solid rgba(191,30,46,0.5)', borderRadius: 4, color: '#fff', fontSize: 11, fontWeight: 700, cursor: exporting ? 'default' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', letterSpacing: 0.3 }}>
            {exporting ? 'Exporting…' : '↓ PDF'}
          </button>
        </div>
      </div>

      {/* Slide */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '28px 40px' }}>
        <div style={{ maxWidth: 1100, width: '100%', margin: '0 auto' }}>
          <ScaledSlide>{renderSlide(current)}</ScaledSlide>
        </div>
        <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: 'rgba(0,0,0,0.28)', letterSpacing: 1 }}>
          {idx + 1} / {SLIDES.length}
        </div>
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: `min(100vw, calc(100vh * ${DESIGN_W / DESIGN_H}))` }}>
            <ScaledSlide><ActiveComponent /></ScaledSlide>
          </div>
          <button onClick={() => setFullscreen(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '6px 14px', fontFamily: 'inherit', letterSpacing: 1 }}>ESC</button>
          <div onClick={() => setCurrent(s => { const i = SLIDES.findIndex(sl => sl.id === s); return i > 0 ? SLIDES[i - 1].id : s; })} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '35%', cursor: 'w-resize' }} />
          <div onClick={() => setCurrent(s => { const i = SLIDES.findIndex(sl => sl.id === s); return i < SLIDES.length - 1 ? SLIDES[i + 1].id : s; })} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '35%', cursor: 'e-resize' }} />
          <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', fontSize: 11, color: 'rgba(255,255,255,0.3)', letterSpacing: 1 }}>
            {idx + 1} / {SLIDES.length}
          </div>
        </div>
      )}

      <style>{`@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@400;600;700;800&display=swap');`}</style>
    </div>
  );
}
